import { useEffect, useState } from 'react';
import { Cradle, MusicalRight } from '@solo/app-core';
import { ButtonsNavBar, DigitalDistributionIcons, Flex, Text } from '@/app/ui';
import { translate } from '@/app/i18n';
import Colors from '@/app/styles/Colors';
import { useCradle } from '@/app/contexts';
import { useBlocState } from '@/app/hooks';

type EaringsByPlatform = {
  amount: number;
  platformName: string;
};

const MOCK_PLATFORM_NAMES = ['Spotify', 'Apple Music', 'Amazon Music', 'Deezer', 'Tidal'];
const MOCK_ALL_AMOUNTS = [10, 7, 6.25, 5, 4.75];
const MOCK_DISTRIBUTION_AMOUNTS = [8.5, 6.2, 5.5, 4.2, 3.8];
const MOCK_PUBLISHING_AMOUNTS = [1.5, 0.8, 0.75, 0.8, 0.95];

const TopRevenueDSP = () => {
  const [activeTab, setActiveTab] = useState<string>();
  const [earningType, setEarningType] = useState<string | undefined>();
  const { consumptionReportsBloc, digitalDistributionBloc, authBloc } = useCradle<Cradle>();
  const authState = useBlocState(authBloc);

  const [earningsByPlatform, setEarningsByPlatform] = useState<EaringsByPlatform[]>([]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async function getEarningsByPlatform() {
      if (authState.kind !== 'AuthenticatedAuthState') return;
      const platforms = await digitalDistributionBloc.getDigitalPlatforms();
      const earnings: EaringsByPlatform[] = [];
      await Promise.all(
        platforms.map(async (platform) => {
          const earn = await consumptionReportsBloc.getEarnings({
            digitalPlatformId: platform.id,
            rightType: earningType ? (earningType as MusicalRight) : undefined,
            userId: authState.user.id,
          });
          earnings.push({
            amount: earn.map((e) => e.amount).reduce((prev, curr) => prev + curr, 0),
            platformName: platform.name,
          });
        }),
      );
      earnings.sort((a, b) => b.amount - a.amount);
      const topEarnings = earnings.slice(0, 5);
      setEarningsByPlatform(topEarnings);
    }
    // getEarningsByPlatform();

    if (!activeTab || activeTab === translate('EARNINGS.ALL')) {
      setEarningsByPlatform(
        MOCK_ALL_AMOUNTS.map((amount, index) => ({
          amount,
          platformName: MOCK_PLATFORM_NAMES[index],
        })),
      );
    } else if (activeTab === translate('DIGITAL_DISTRIBUTION.DIGITAL_DISTRIBUTION')) {
      setEarningsByPlatform(
        MOCK_DISTRIBUTION_AMOUNTS.map((amount, index) => ({
          amount,
          platformName: MOCK_PLATFORM_NAMES[index],
        })),
      );
    } else if (activeTab === translate('EARNINGS.PUBLISHING')) {
      setEarningsByPlatform(
        MOCK_PUBLISHING_AMOUNTS.map((amount, index) => ({
          amount,
          platformName: MOCK_PLATFORM_NAMES[index],
        })),
      );
    }
  }, [activeTab]);

  function changeTab(tab: string) {
    switch (tab) {
      case translate('EARNINGS.ALL'):
        setEarningType(undefined);
        break;
      case translate('DIGITAL_DISTRIBUTION.DIGITAL_DISTRIBUTION'):
        setEarningType(MusicalRight.DISTRIBUTION);
        break;
      default:
        setEarningType(MusicalRight.PUBLISHING);
        break;
    }
    setActiveTab(tab);
  }

  return (
    <Flex.Container gap="1rem" flexDirection="column">
      <ButtonsNavBar
        defaultTab={translate('EARNINGS.ALL')}
        tabs={[
          translate('EARNINGS.ALL'),
          translate('DIGITAL_DISTRIBUTION.DIGITAL_DISTRIBUTION'),
          translate('EARNINGS.PUBLISHING'),
        ]}
        onSelectTab={(tab) => changeTab(tab)}
      />
      <Flex.Container flexDirection="column">
        {earningsByPlatform.map((earning, index) => (
          <Flex.Container
            gap="1rem"
            key={`${earning.platformName} ${earning.amount}`}
            style={{ borderTop: index !== 0 ? `1px solid ${Colors.borderInput}` : '' }}
            padding="1rem 0"
          >
            <Flex.Container gap="1rem">
              <DigitalDistributionIcons
                icon={earning.platformName.toLowerCase().replace(' ', '-')}
                width={24}
              />
              <Text.LabelMedium style={{ paddingTop: '3px' }}>
                {earning.platformName}
              </Text.LabelMedium>
            </Flex.Container>
            <Flex.Container
              style={{ width: '50%', marginLeft: 'auto' }}
              alignItems="flex-end"
              flexDirection="column"
            >
              <Text.Span>{earning.amount}€</Text.Span>
            </Flex.Container>
          </Flex.Container>
        ))}
      </Flex.Container>
    </Flex.Container>
  );
};

export default TopRevenueDSP;
